/**
 * Pestañas de categoría en la búsqueda (`/s`, `/s/todos`, `/s/jardines`, `/s/interiores`).
 *
 * Centraliza la ruta y el filtro Flex para no repetir el switch en el mapa, la
 * paginación, el topbar y el duck.
 */

import { isHasGardenEnabled, listingFaces, spaceTypeOf } from './listingFaces';
import { COVERED_AREA_EXTRA, WEATHER_RAIN, isProtectedFrom } from './weatherProtection';

export { COVERED_AREA_EXTRA };

export const SEARCH_TAB_TODOS = 'todos';
export const SEARCH_TAB_POOL = 'pool';
export const SEARCH_TAB_GARDEN = 'jardines';
export const SEARCH_TAB_INTERIORES = 'interiores';

/** ¿Es la pestaña que mezcla todos los tipos de espacio? */
export const isTodosSearchTab = tab => tab === SEARCH_TAB_TODOS;

/** ¿Es la pestaña de jardines (incl. alias)? */
export const isGardenSearchTab = tab => tab === SEARCH_TAB_GARDEN || tab === 'garden';

/** ¿Es la pestaña de interiores? */
export const isInterioresSearchTab = tab =>
  tab === SEARCH_TAB_INTERIORES || tab === 'interior' || tab === 'interiors';

/** ¿Es la pestaña de piscinas (ruta `/s` sin tab, o tab vacío/`pool`)? */
export const isPoolSearchTab = tab =>
  !tab || tab === SEARCH_TAB_POOL || tab === '' || tab === 'piscinas';

/**
 * Ruta nombrada + params de path para una pestaña de búsqueda.
 * @returns {{ routeName: string, params: Object }}
 */
export const routeForSearchTab = tab => {
  if (isTodosSearchTab(tab) || tab === 'all') {
    return { routeName: 'SearchBasePage', params: { tab: SEARCH_TAB_TODOS } };
  }
  if (isGardenSearchTab(tab)) {
    return { routeName: 'SearchBasePage', params: { tab: SEARCH_TAB_GARDEN } };
  }
  if (isInterioresSearchTab(tab)) {
    return { routeName: 'SearchBasePage', params: { tab: SEARCH_TAB_INTERIORES } };
  }
  return { routeName: 'SearchPage', params: {} };
};

/**
 * Valor de `pub_searchListingType` para Flex, o `null` si la pestaña no filtra por tipo
 * (Todos: entran piscina, jardín e interiores).
 */
export const pubSearchListingTypeForTab = tab => {
  if (isTodosSearchTab(tab) || tab === 'all') {
    return null;
  }
  if (isGardenSearchTab(tab)) {
    return 'garden';
  }
  if (isInterioresSearchTab(tab)) {
    return 'interiores';
  }
  return 'pool';
};

const INTERIORES_SPACE_TYPES = ['interiores', 'indoors', 'interior', 'interiors'];

/** ¿Es un anuncio de interiores? Acepta los aliases legacy y la clave antigua `category`. */
export const isInterioresListing = listing => INTERIORES_SPACE_TYPES.includes(spaceTypeOf(listing));

const hasCoveredAreaExtra = publicData =>
  Array.isArray(publicData?.extras) && publicData.extras.includes(COVERED_AREA_EXTRA);

/** Opciones del campo `extras` de Console: "Comedor interior" y "Salón interior". */
export const INDOOR_SPACE_EXTRAS = ['comedor', 'salon'];

const extrasKeys = extras =>
  Array.isArray(extras) ? extras : typeof extras === 'string' && extras ? [extras] : [];

const hasIndoorSpaceExtra = publicData =>
  INDOOR_SPACE_EXTRAS.some(key => extrasKeys(publicData?.extras).includes(key));

/**
 * ¿Es "apto para lluvia"? El anfitrión declara protección frente a la lluvia
 * (`publicData.weatherProtectedFor`, ver docs/weather-protection.md), tiene "Zona techada" en
 * Extras, o tiene espacio interior (hasIndoorSpaceListing: espacios de la pestaña Espacios,
 * incluidos o de pago, "Comedor interior" / "Salón interior" en Extras, o interiores). La misma
 * regla decide el chip de la búsqueda (filterResponseByShelterChips), el distintivo de la
 * tarjeta y la fila de lluvia de la ficha. Ver docs/search-filters.md.
 */
export const isRainProofListing = listing => {
  const publicData = listing?.attributes?.publicData;
  return (
    isProtectedFrom(publicData, WEATHER_RAIN) ||
    hasCoveredAreaExtra(publicData) ||
    hasIndoorSpaceListing(listing)
  );
};

/**
 * ¿Tiene espacio interior? `publicData.hasIndoorSpace`, o lleva en Extras "Comedor interior"
 * (`comedor`) o "Salón interior" (`salon`), o es un anuncio de interiores. Decide el chip
 * "Con espacio interior".
 */
export const hasIndoorSpaceListing = listing => {
  const publicData = listing?.attributes?.publicData;
  return (
    publicData?.hasIndoorSpace === true ||
    hasIndoorSpaceExtra(publicData) ||
    isInterioresListing(listing)
  );
};

/**
 * ¿Encaja este anuncio en la pestaña tipada?
 *
 * Flex debería filtrar con `pub_searchListingType`, pero si el índice de búsqueda no
 * aplica el filtro (devuelve el universo entero), esto es la red de seguridad
 * client-side — igual que el filtro "Con descuento".
 *
 * El tipo se lee con `spaceTypeOf` (`searchListingType`, o la clave antigua `category`),
 * igual que las caras: si no, un jardín antiguo con solo `category` salía en Piscinas
 * pintado como jardín y faltaba en Jardines. Interiores acepta aliases legacy
 * (`indoors` / `interior`). Jardines incluye las piscinas con cara de jardín
 * (`hasGarden`), que es lo que la pestaña promete.
 *
 * @param {Object} listing
 * @param {string|null} searchListingType  valor de `pubSearchListingTypeForTab`
 * @returns {boolean}
 */
export const listingMatchesSearchListingType = (listing, searchListingType) => {
  if (!searchListingType) {
    return true;
  }
  const type = spaceTypeOf(listing);

  if (searchListingType === 'garden') {
    if (type === 'garden') {
      return true;
    }
    // Pools that also offer a garden face (same rule as the dual Flex query).
    return (
      (type === 'pool' || type == null) &&
      isHasGardenEnabled(listing?.attributes?.publicData?.hasGarden)
    );
  }
  if (searchListingType === 'interiores') {
    return isInterioresListing(listing);
  }
  if (searchListingType === 'pool') {
    // A pool whose pool face ops switched off (`poolOffered: false`) is garden-only.
    return (type === 'pool' || type == null) && listingFaces(listing).hasPool;
  }
  return type === searchListingType;
};

/** Tab canónica desde el path de la URL (`/s/jardines` → `jardines`). */
export const searchTabFromPathname = pathname => {
  const path = String(pathname || '');
  if (path.startsWith('/s/todos')) {
    return SEARCH_TAB_TODOS;
  }
  if (path.startsWith('/s/jardines')) {
    return SEARCH_TAB_GARDEN;
  }
  if (path.startsWith('/s/interiores')) {
    return SEARCH_TAB_INTERIORES;
  }
  return SEARCH_TAB_POOL;
};
